import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Loader2, Users, Globe, Fingerprint, ShieldAlert, Activity,
  Wifi, TrendingUp, RefreshCw, AlertTriangle, Ban,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { AdminBlockedAttemptsPanel } from "@/components/admin/AdminBlockedAttemptsPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const truncFp = (fp) => (!fp ? "—" : fp.length > 14 ? `${fp.slice(0, 6)}…${fp.slice(-4)}` : fp);

const StatCard = ({ icon: Icon, label, value, hint, color = "text-[#d4af37]", testid }) => (
  <div
    data-testid={testid}
    className="glass-dark border border-white/10 rounded-xl p-3 sm:p-4 hover:border-[#d4af37]/40 transition-colors"
  >
    <div className="flex items-center justify-between mb-1">
      <span className="text-[10px] uppercase tracking-widest text-neutral-400">{label}</span>
      <Icon className={`w-4 h-4 ${color}`} />
    </div>
    <div className={`text-2xl sm:text-3xl font-serif ${color}`}>{value}</div>
    {hint && <div className="text-[10px] text-neutral-500 mt-0.5">{hint}</div>}
  </div>
);

/**
 * ActivityChart — pure-CSS bar chart. No external chart library needed for
 * this simple daily overview.
 */
const ActivityChart = ({ data }) => {
  if (!data?.length) {
    return (
      <div className="text-xs text-neutral-500 italic p-4 text-center">
        Sin actividad en el periodo.
      </div>
    );
  }
  const maxSessions = Math.max(1, ...data.map((d) => d.sessions || 0));

  return (
    <div className="flex items-end gap-1 h-32 sm:h-40 overflow-x-auto pb-1" data-testid="activity-chart">
      {data.map((d) => {
        const pct = Math.max(6, ((d.sessions || 0) / maxSessions) * 100);
        const label = (d.day || "").slice(5); // MM-DD
        return (
          <div
            key={d.day}
            className="flex flex-col items-center gap-1 flex-1 min-w-[26px]"
            title={`${d.day}\nSesiones: ${d.sessions}\nUsuarios únicos: ${d.unique_users}\nIPs únicas: ${d.unique_ips}\nRegistros: ${d.registers}`}
          >
            <div
              className="w-full bg-gradient-to-t from-[#d4af37] to-[#f1c869] rounded-t hover:from-[#f1c869] hover:to-[#ffd982] transition-all"
              style={{ height: `${pct}%` }}
            />
            <span className="text-[9px] text-neutral-500 whitespace-nowrap">{label}</span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * AdminAccessStatsPanel — Access analytics dashboard.
 *
 * Pulls /api/admin/access-stats and renders:
 *   1. Top-level counters (unique IPs, FPs, sessions, active 24h/7d).
 *   2. Daily activity bar chart (last N days).
 *   3. Fraud signals:
 *      - Multi-user IPs (same IP → many users)
 *      - Multi-IP users (same user → many IPs = possible VPN)
 *   4. Top IPs + top fingerprints tables.
 *
 * Every row that hints at fraud has a quick "Bloquear IP" button that calls
 * POST /admin/banned-devices.
 */
export const AdminAccessStatsPanel = () => {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/access-stats?days=${days}`, {
        headers: authHeader(),
      });
      setData(r.data);
    } catch {
      setData(null);
      toast.error("No se pudo cargar el panel de accesos");
    } finally {
      setLoading(false);
    }
  }, [authHeader, days]);

  useEffect(() => { load(); }, [load]);

  const banIp = async (ip, reason) => {
    if (!ip) return;
    if (!window.confirm(`¿Bloquear la IP ${ip}? Los usuarios legítimos que compartan esa IP no podrán acceder.`)) return;
    try {
      await axios.post(`${API}/admin/banned-devices`, {
        ip,
        reason: reason || `Bloqueo manual desde panel de accesos`,
      }, { headers: authHeader() });
      toast.success(`IP ${ip} bloqueada`);
      load();
    } catch {
      toast.error("No se pudo bloquear la IP");
    }
  };

  const banFingerprint = async (fp) => {
    if (!fp) return;
    if (!window.confirm(`¿Bloquear el dispositivo con huella ${truncFp(fp)}?`)) return;
    try {
      await axios.post(`${API}/admin/banned-devices`, {
        fingerprint: fp,
        reason: "Bloqueo manual desde panel de accesos",
      }, { headers: authHeader() });
      toast.success("Dispositivo bloqueado");
      load();
    } catch {
      toast.error("No se pudo bloquear el dispositivo");
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#d4af37] mx-auto" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="py-8 text-center text-sm text-neutral-500 italic">
        Sin datos disponibles.
      </div>
    );
  }

  const t = data.totals || {};

  return (
    <div className="space-y-5" data-testid="access-stats-panel">
      <AdminBlockedAttemptsPanel />
      {/* Header + controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h2 className="font-serif text-xl text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#d4af37]" />
            Panel de Accesos
          </h2>
          <p className="text-[11px] text-neutral-500">
            Rastreo de IPs, dispositivos y detección de patrones sospechosos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-black/40 border border-white/10 rounded px-2 py-1.5 text-xs text-neutral-200"
            data-testid="access-stats-period"
          >
            <option value={7}>Últimos 7 días</option>
            <option value={14}>Últimos 14 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
          </select>
          <button
            onClick={load}
            className="text-xs px-2 py-1.5 rounded border border-white/10 hover:border-[#d4af37]/60 text-neutral-300 hover:text-white flex items-center gap-1"
            data-testid="access-stats-refresh"
          >
            <RefreshCw className="w-3 h-3" /> Actualizar
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
        <StatCard testid="kpi-users" icon={Users} label="Usuarios" value={t.total_users || 0} />
        <StatCard testid="kpi-active-24h" icon={TrendingUp} label="Activos 24h" value={t.active_24h || 0} color="text-emerald-300" />
        <StatCard testid="kpi-active-7d" icon={TrendingUp} label="Activos 7d" value={t.active_7d || 0} color="text-cyan-300" />
        <StatCard testid="kpi-ips" icon={Globe} label={`IPs únicas ${data.period_days}d`} value={t.unique_ips || 0} />
        <StatCard testid="kpi-fps" icon={Fingerprint} label={`Dispositivos ${data.period_days}d`} value={t.unique_fingerprints || 0} />
        <StatCard testid="kpi-banned" icon={Ban} label="Baneados" value={t.banned_devices || 0} color={t.banned_devices ? "text-red-300" : "text-neutral-400"} />
      </div>

      {/* Activity chart */}
      <div className="glass-dark border border-white/10 rounded-xl p-3 sm:p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-white font-medium flex items-center gap-2">
            <Activity className="w-4 h-4 text-[#d4af37]" /> Actividad diaria
          </span>
          <span className="text-[10px] text-neutral-500">
            {t.total_sessions || 0} sesiones · {data.period_days} días
          </span>
        </div>
        <ActivityChart data={data.daily_activity} />
      </div>

      {/* Fraud signals: multi-user IPs */}
      {(data.multi_user_ips?.length > 0) && (
        <div className="glass-dark border border-amber-500/30 rounded-xl p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-white font-medium">
              Alerta — misma IP con múltiples usuarios
            </span>
            <span className="text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">
              posible fraude · {data.multi_user_ips.length}
            </span>
          </div>
          <div className="rounded-xl overflow-hidden border border-white/5">
            <table className="w-full text-xs">
              <thead className="bg-black/30 text-[10px] uppercase tracking-widest text-neutral-400">
                <tr>
                  <th className="px-3 py-2 text-left">IP</th>
                  <th className="px-3 py-2 text-left">Usuarios</th>
                  <th className="px-3 py-2 text-left">Sesiones</th>
                  <th className="px-3 py-2 text-left">Última vez</th>
                  <th className="px-3 py-2 text-right">Acción</th>
                </tr>
              </thead>
              <tbody>
                {data.multi_user_ips.map((r, i) => (
                  <tr key={i} className="border-t border-white/5" data-testid={`multi-user-ip-${i}`}>
                    <td className="px-3 py-2 text-white font-mono">{r.ip}</td>
                    <td className="px-3 py-2 text-neutral-300">
                      <span className="text-amber-300 font-medium">{r.user_count}</span>{" "}
                      <span className="text-neutral-500 text-[10px]">
                        ({(r.users || []).slice(0, 3).map((u) => `${u.username}${u.name ? ` (${u.name})` : ""}`).join(", ")}
                        {(r.users || []).length > 3 ? "…" : ""})
                      </span>
                    </td>
                    <td className="px-3 py-2 text-neutral-300">{r.sessions}</td>
                    <td className="px-3 py-2 text-neutral-400 whitespace-nowrap">{fmt(r.last_seen)}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => banIp(r.ip, `Fraude sospechado — ${r.user_count} usuarios compartiendo IP`)}
                        className="text-[10px] px-2 py-1 rounded border border-red-500/30 text-red-300 hover:bg-red-500/10 hover:border-red-500/60"
                        data-testid={`ban-ip-${r.ip}`}
                      >
                        <ShieldAlert className="w-3 h-3 inline mr-1" /> Bloquear
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Fraud signals: multi-IP users (VPN) */}
      {(data.multi_ip_users?.length > 0) && (
        <div className="glass-dark border border-indigo-500/30 rounded-xl p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <Wifi className="w-4 h-4 text-indigo-400" />
            <span className="text-sm text-white font-medium">
              Usuarios con muchas IPs (posible VPN o cuenta compartida)
            </span>
            <span className="text-[10px] text-indigo-300 bg-indigo-500/10 border border-indigo-500/30 rounded-full px-2 py-0.5">
              {data.multi_ip_users.length}
            </span>
          </div>
          <div className="rounded-xl overflow-hidden border border-white/5">
            <table className="w-full text-xs">
              <thead className="bg-black/30 text-[10px] uppercase tracking-widest text-neutral-400">
                <tr>
                  <th className="px-3 py-2 text-left">Usuario</th>
                  <th className="px-3 py-2 text-left">IPs</th>
                  <th className="px-3 py-2 text-left">Sesiones</th>
                  <th className="px-3 py-2 text-left">Última vez</th>
                  <th className="px-3 py-2 text-left">Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.multi_ip_users.map((r, i) => (
                  <tr key={i} className="border-t border-white/5" data-testid={`multi-ip-user-${r.user_id}`}>
                    <td className="px-3 py-2">
                      <div className="text-white">{r.username}</div>
                      <div className="text-[10px] text-neutral-500">{r.name}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-indigo-300 font-medium">{r.ip_count}</span>
                      <div className="text-[9px] text-neutral-500 font-mono mt-0.5">
                        {(r.sample_ips || []).slice(0, 3).join(", ")}
                        {(r.sample_ips || []).length > 3 ? "…" : ""}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-neutral-300">{r.sessions}</td>
                    <td className="px-3 py-2 text-neutral-400 whitespace-nowrap">{fmt(r.last_seen)}</td>
                    <td className="px-3 py-2">
                      {r.blocked ? (
                        <span className="text-red-300 text-[10px] bg-red-500/10 border border-red-500/30 rounded-full px-2 py-0.5">bloqueado</span>
                      ) : (
                        <span className="text-emerald-300 text-[10px]">activo</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top IPs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass-dark border border-white/10 rounded-xl p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-4 h-4 text-[#d4af37]" />
            <span className="text-sm text-white font-medium">Top IPs por actividad</span>
          </div>
          <div className="rounded-xl overflow-hidden border border-white/5 max-h-80 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-black/80 backdrop-blur">
                <tr className="text-[10px] uppercase tracking-widest text-neutral-400">
                  <th className="px-2 py-2 text-left">IP</th>
                  <th className="px-2 py-2 text-left">Ses</th>
                  <th className="px-2 py-2 text-left">Usuario · Nombre</th>
                  <th className="px-2 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {(data.top_ips || []).map((r, i) => (
                  <tr key={i} className="border-t border-white/5" data-testid={`top-ip-row-${i}`}>
                    <td className="px-2 py-2 text-white font-mono">{r.ip}</td>
                    <td className="px-2 py-2 text-neutral-300">{r.sessions}</td>
                    <td className="px-2 py-2 text-neutral-300">
                      <div className="flex flex-wrap gap-1">
                        {(r.users || []).slice(0, 4).map((u) => (
                          <span key={u.id} className={`text-[10px] px-1.5 py-0.5 rounded border ${u.role === "admin" ? "border-amber-500/40 text-amber-300" : "border-white/10 text-neutral-200"}`} title={u.visible_id || ""}>
                            {u.username}{u.name ? <span className="text-neutral-500"> · {u.name}</span> : null}
                          </span>
                        ))}
                        {(r.users || []).length > 4 && <span className="text-[10px] text-neutral-500">+{r.users.length - 4}</span>}
                      </div>
                    </td>
                    <td className="px-2 py-2 text-right">
                      {r.ban_candidate ? (
                        <button
                          onClick={() => banIp(r.ip)}
                          className="text-[10px] px-1.5 py-0.5 rounded border border-red-500/30 text-red-300 hover:bg-red-500/10"
                          data-testid={`quick-ban-ip-${r.ip}`}
                        >
                          <Ban className="w-3 h-3" />
                        </button>
                      ) : (
                        <span className="text-[9px] text-neutral-600">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top fingerprints */}
        <div className="glass-dark border border-white/10 rounded-xl p-3 sm:p-4">
          <div className="flex items-center gap-2 mb-2">
            <Fingerprint className="w-4 h-4 text-[#d4af37]" />
            <span className="text-sm text-white font-medium">Top dispositivos (fingerprint)</span>
          </div>
          <div className="rounded-xl overflow-hidden border border-white/5 max-h-80 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-black/80 backdrop-blur">
                <tr className="text-[10px] uppercase tracking-widest text-neutral-400">
                  <th className="px-2 py-2 text-left">Huella</th>
                  <th className="px-2 py-2 text-left">Ses</th>
                  <th className="px-2 py-2 text-left">Users</th>
                  <th className="px-2 py-2 text-left">IPs</th>
                  <th className="px-2 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {(data.top_fingerprints || []).map((r, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="px-2 py-2 text-white font-mono" title={r.fingerprint}>
                      {truncFp(r.fingerprint)}
                    </td>
                    <td className="px-2 py-2 text-neutral-300">{r.sessions}</td>
                    <td className="px-2 py-2 text-neutral-300">
                      {r.user_count}
                      {(r.users || []).length > 0 && (
                        <div className="text-[10px] text-neutral-500 leading-tight">{r.users.slice(0, 3).map((u) => `${u.username}${u.name ? ` (${u.name})` : ""}`).join(", ")}</div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-neutral-300">{r.ip_count}</td>
                    <td className="px-2 py-2 text-right">
                      <button
                        onClick={() => banFingerprint(r.fingerprint)}
                        className="text-[10px] px-1.5 py-0.5 rounded border border-red-500/30 text-red-300 hover:bg-red-500/10"
                        data-testid={`quick-ban-fp-${i}`}
                      >
                        <Ban className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Recent accesses — who logged in from which IP */}
      <div className="glass-dark border border-white/10 rounded-xl p-3 sm:p-4" data-testid="recent-sessions-panel">
        <div className="flex items-center gap-2 mb-2">
          <Globe className="w-4 h-4 text-[#d4af37]" />
          <span className="text-sm text-white font-medium">Últimos accesos</span>
          <span className="text-[10px] text-neutral-500">IP · usuario · nombre · origen</span>
        </div>
        <div className="rounded-xl overflow-x-auto border border-white/5 max-h-96 overflow-y-auto">
          <table className="w-full text-xs min-w-[640px]">
            <thead className="sticky top-0 bg-black/80 backdrop-blur">
              <tr className="text-[10px] uppercase tracking-widest text-neutral-400">
                <th className="px-2 py-2 text-left">Fecha</th>
                <th className="px-2 py-2 text-left">IP</th>
                <th className="px-2 py-2 text-left">Usuario</th>
                <th className="px-2 py-2 text-left">Nombre</th>
                <th className="px-2 py-2 text-left">ID</th>
                <th className="px-2 py-2 text-left">Origen</th>
                <th className="px-2 py-2 text-left">Dispositivo</th>
              </tr>
            </thead>
            <tbody>
              {(data.recent_sessions || []).length === 0 && (
                <tr><td colSpan={7} className="px-3 py-4 text-center text-neutral-500 italic">Sin accesos en el periodo.</td></tr>
              )}
              {(data.recent_sessions || []).map((r, i) => (
                <tr key={i} className="border-t border-white/5" data-testid={`recent-session-${i}`}>
                  <td className="px-2 py-1.5 text-neutral-400 whitespace-nowrap">{fmt(r.at)}</td>
                  <td className="px-2 py-1.5 text-white font-mono">{r.ip}</td>
                  <td className={`px-2 py-1.5 font-medium ${r.role === "admin" ? "text-amber-300" : r.blocked ? "text-red-300" : "text-neutral-100"}`}>{r.username}</td>
                  <td className="px-2 py-1.5 text-neutral-300">{r.name}</td>
                  <td className="px-2 py-1.5 text-[#d4af37] font-mono text-[10px]">{r.visible_id || "—"}</td>
                  <td className="px-2 py-1.5 text-neutral-400">{r.source}</td>
                  <td className="px-2 py-1.5 text-neutral-500 truncate max-w-[220px]" title={r.user_agent}>{r.user_agent || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[10px] text-neutral-600 italic border-t border-white/5 pt-3">
        {data.notice}
      </div>
    </div>
  );
};

export default AdminAccessStatsPanel;
